import {useSession} from '../../session';
import {useState, type FormEvent} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useNavigate, useParams} from 'react-router-dom';
import {AppFrame, Button, Card, PageHeader} from '@pocketbuy/outdoor-design-system';
import {ApiError} from '@pocketbuy/outdoor-api-client';
import type {StockPoint} from '@pocketbuy/outdoor-business-types';
import {authApi, currentPrincipal, inventoryApi} from '../../api';

export function StockPointListPage() {
  const nav = useNavigate();
  const activeRole=useSession(s=>s.session?.activeRole);
  const [search, setSearch] = useState('');
  const points = useQuery({queryKey: ['stock-points'], queryFn: inventoryApi.stockPoints});
  const visible = (points.data ?? []).filter(p => `${p.name} ${p.address ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()));
  return <AppFrame>
    <PageHeader title="Stock points" eyebrow="Manage" onBack={() => nav(activeRole==='OPERATIONS'?'/manage':'/home')}/>
    <div className="pb-stack">
      <div className="op-page-actions"><Button onClick={() => nav('/stock-points/new')}>New stock point</Button></div>
      <label className="pb-field">Search stock points<input type="search" autoComplete="off" value={search} onChange={e => setSearch(e.target.value)} placeholder="Name or address"/></label>
      {points.isPending && <div className="op-skeleton"><i/><i/></div>}
      {points.isError && <Card><p className="op-error">Stock points could not be loaded.</p><Button onClick={() => void points.refetch()}>Try again</Button></Card>}
      {points.isSuccess && <Card className="op-list">{visible.map(point => <button type="button" className="op-list-item" key={point.id} onClick={() => nav(`/stock-points/${point.id}`)}>
        <span className="op-list-copy"><strong>{point.name}</strong><small>{point.address || 'Address not added'}</small></span><span aria-hidden="true">›</span>
      </button>)}{!visible.length && <div className="op-empty"><b>{search ? 'No matching stock points' : 'No stock points yet'}</b><small>{search ? 'Try another name or address.' : 'Create a stock point to assign to your team.'}</small></div>}</Card>}
    </div>
  </AppFrame>;
}

export function StockPointEditPage() {
  const {id} = useParams();
  const nav = useNavigate();
  const creating = id === 'new';
  const points = useQuery({queryKey: ['stock-points'], queryFn: inventoryApi.stockPoints, enabled: !creating});
  const point = points.data?.find(p => p.id === Number(id));
  return <AppFrame><PageHeader title={creating ? 'New stock point' : 'Edit stock point'} eyebrow="Stock points" onBack={() => nav('/stock-points')}/>
    {creating || point ? <StockPointForm key={id} point={point}/> : points.isError ? <Card><p className="op-error">Stock point could not be loaded.</p><Button onClick={() => void points.refetch()}>Try again</Button></Card> : points.isPending ? <div className="op-skeleton"><i/><i/></div> : <p role="alert">Stock point not found.</p>}
  </AppFrame>;
}

function StockPointForm({point}: {point?: StockPoint}) {
  const nav = useNavigate(), client = useQueryClient();
  const principal = useQuery({queryKey: ['current-principal'], queryFn: currentPrincipal});
  const countryCode = principal.data?.countryCode;
  const countries = useQuery({queryKey: ['auth-countries'], queryFn: authApi.countries});
  const country = countries.data?.find(c => c.countryCode === (point?.countryCode ?? countryCode));
  const [name, setName] = useState(point?.name ?? '');
  const [address, setAddress] = useState(point?.address ?? '');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  async function save(event: FormEvent) {
    event.preventDefault(); if (busy) return;
    setBusy(true); setError('');
    try {
      const values = {name: name.trim(), address: address.trim()};
      if (point) await inventoryApi.updateStockPoint(point.id, values);
      else await inventoryApi.createStockPoint(values);
      await Promise.all([client.invalidateQueries({queryKey: ['stock-points']}), client.invalidateQueries({queryKey: ['sales-clusters']})]);
      nav('/stock-points');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the stock point. Try again.');
    } finally {setBusy(false);}
  }
  return <form className="pb-stack" onSubmit={save}>
    <Card className="pb-stack">
      <div className="pb-field"><span>Country</span><strong>{country?.countryName ?? countryCode ?? 'Loading…'}</strong></div>
      <label className="pb-field">Stock point name<input required maxLength={160} value={name} disabled={busy} onChange={e => setName(e.target.value)}/></label>
      <label className="pb-field"><span className="pb-field-label">Address <span className="pb-muted">Optional</span></span><textarea rows={3} maxLength={500} value={address} disabled={busy} onChange={e => setAddress(e.target.value)}/></label>
    </Card>
    {error && <p role="alert" className="op-error">{error}</p>}
    {principal.isError && <p role="alert" className="op-error">Country could not be loaded. <button type="button" className="op-link" onClick={() => void principal.refetch()}>Retry</button></p>}
    <div className="op-task-footer"><Button className="pb-full" disabled={busy || !name.trim() || !countryCode}>{busy ? 'Saving…' : 'Save'}</Button></div>
  </form>;
}
